// Canonical read model for a floor's design layout — the plan the layout
// designer writes, and which every other view of the floor must read back the
// same way.
//
// Two rules a reader has to honour, both of which are easy to lose:
//
//   - A room can span a rectangle of cells. The cells it swallowed are not
//     deleted; they still exist and point back at the anchor through
//     `absorbedBy`, which is what makes a merge reversible. A grid must skip
//     absorbed cells and let the anchor span over them, or a merged room draws
//     twice.
//   - `cellType` says what the cell IS. Only a room takes desks; corridors,
//     lobbies, entrances, voids and the lift/stair cores never do.

// A cell with no explicit type is a plain room — the same default the designer
// and the validator use.
export const DEFAULT_CELL_TYPE = "EMPTY";

export const CELL_ROLES = {
  EMPTY: { label: "Room", acceptsDesks: true },
  CORRIDOR: { label: "Corridor", acceptsDesks: false },
  LOBBY: { label: "Lobby", acceptsDesks: false },
  ENTRANCE: { label: "Entrance", acceptsDesks: false },
  VOID: { label: "Void", acceptsDesks: false },
  ELEVATOR: { label: "Lift", acceptsDesks: false },
  STAIRS: { label: "Stairs", acceptsDesks: false },
};

export const planCellKey = (row, column) => `${row}-${column}`;

const toPositiveInt = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : fallback;
};

const toCoordinate = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * One room row from `getFloorWithRelations` as a plan cell. The service already
 * normalises field names; this keeps the layout geometry together so no caller
 * has to remember that dropping `row_span` silently flattens saved merges.
 */
export function readRoomCell(room) {
  return {
    roomId: room?.room_id ?? room?.id ?? null,
    roomCode: room?.room_code ?? "",
    name: room?.name ?? "",
    row: toCoordinate(room?.row),
    column: toCoordinate(room?.column),
    cellType:
      String(room?.cellType || "").toUpperCase() || DEFAULT_CELL_TYPE,
    rowSpan: toPositiveInt(room?.row_span, 1),
    colSpan: toPositiveInt(room?.col_span, 1),
    absorbedBy: room?.absorbed_by_room_id ?? null,
    spaceTypeId: room?.space_type_id ?? null,
    tables: Array.isArray(room?.tables) ? room.tables : [],
  };
}

/** Rooms indexed by `${row}-${column}`, absorbed cells included. */
export function toPlanCells(rooms) {
  const cells = new Map();
  (Array.isArray(rooms) ? rooms : []).forEach((room) => {
    const cell = readRoomCell(room);
    if (cell.row == null || cell.column == null) return;
    cells.set(planCellKey(cell.row, cell.column), cell);
  });
  return cells;
}

export const roleOf = (cell) =>
  CELL_ROLES[cell?.cellType] ?? CELL_ROLES[DEFAULT_CELL_TYPE];

export const isAbsorbed = (cell) => cell?.absorbedBy != null;

/** Only an unabsorbed room takes desks. */
export const acceptsDesks = (cell) =>
  Boolean(cell) && !isAbsorbed(cell) && roleOf(cell).acceptsDesks;
